import * as d3 from 'd3';

import { Size, Margin } from '../../visTypes';

import { exportSvgAsPng, ISharedD3ChartProps } from '../../vis-shared';

export interface IGaugeColorThreshold {
  color: string;
  percentage: number;
}

export type ColorConfig = string | ((d: number) => string) | IGaugeColorThreshold[];

export interface IGaugeConfig extends ISharedD3ChartProps {
  target: any;
  percentage: number;
  width?: number;
  color?: ColorConfig;
  backgroundColor?: string;
  label?: string | [string] | [string, string];
}

const defaultColorScale = [
  { color: '#D7354A', percentage: 40 },
  { color: '#F9D118', percentage: 60 },
  { color: '#309D82', percentage: 80 },
  { color: '#008765', percentage: 90 },
];

function getLevelColor(colorArray: IGaugeColorThreshold[], percentage: number) {
  // eslint-disable-next-line
  for (let i = 0; i < colorArray.length; i++) {
    if (colorArray[i].percentage > percentage) {
      return colorArray[i].color;
    }
  }
  return colorArray[colorArray.length - 1].color;
}

export class Gauge {
  target: any;
  percentage: number;
  label: string | string[] | undefined;
  width: number;
  radius: number;
  currentColor: string;
  color: ColorConfig;
  backgroundColor: string;
  size: Size;
  borderWidth: number;
  chartSize: Size;
  margin: Margin;
  svg!: d3.Selection<any, any, any, any>;
  gaugeSelection!: d3.Selection<SVGGElement, any, any, any>;
  backgroundSelection!: d3.Selection<SVGGElement, any, any, any>;
  foregroundSelection!: d3.Selection<SVGGElement, any, any, any>;
  labelSelection!: d3.Selection<SVGGElement, any, any, any>;
  transition: d3.TransitionLike<any, any>;
  exportTitle: string | undefined;
  exportFileName: string;

  constructor(config: IGaugeConfig) {
    this.target = config.target;
    this.percentage = config.percentage;
    this.label = config.label;
    this.backgroundColor = config.backgroundColor || '#EAEFF9';
    this.color = config.color || defaultColorScale;
    this.exportTitle = config.exportTitle;
    this.exportFileName = config.exportFileName || 'Gauge';
    this.currentColor = this.getCurrentColor();
    const domWidth = Math.floor(parseInt(d3.select(this.target).style('width'), 10));
    const width = config.width || domWidth;
    this.width = width;
    this.size = {
      width,
      height: width,
    };
    this.borderWidth = width / 20;
    this.radius = width / 2 - this.borderWidth;
    const marginAll = this.borderWidth;
    this.margin = {
      top: marginAll,
      bottom: marginAll,
      left: marginAll,
      right: marginAll,
    };
    this.chartSize = {
      width: this.size.width - this.margin.left - this.margin.right,
      height: this.size.height - this.margin.top - this.margin.bottom,
    };
    this.transition = d3.transition();
    this.draw();
  }

  getCurrentColor(): string {
    if (typeof this.color === 'string') {
      return this.color;
    }
    if (typeof this.color === 'function') {
      return this.color(this.percentage);
    }
    if (typeof this.color === 'object') {
      const sortedLevels = this.color.sort((a: IGaugeColorThreshold, b: IGaugeColorThreshold) => a.percentage - b.percentage);
      return getLevelColor(sortedLevels, this.percentage);
    }
    return '#309D82'; // green
  }

  draw() {
    const css = `
      text { font-family: Lato, sans-serif }
    `;
    // initial drawing of SVG components
    if (!this.svg) {
      this.svg = d3.select(this.target).append('svg').classed('gauge', true).attr('height', this.size.height).attr('width', this.size.width);
      this.svg.append('style').text(css);
      this.gaugeSelection = this.svg
        .append('g')
        .attr('transform', `translate(${this.margin.left + this.chartSize.width / 2}, ${this.margin.top + this.chartSize.height / 2})`);
      this.backgroundSelection = this.gaugeSelection.append('g');
      this.foregroundSelection = this.gaugeSelection.append('g');
      this.labelSelection = this.gaugeSelection.append('g');
    }
    this.drawGauge();
  }

  drawGauge() {
    this.backgroundSelection.html('');
    this.backgroundSelection
      .append('circle')
      .attr('r', this.radius)
      .style('stroke', this.backgroundColor)
      .style('fill', 'none')
      .style('stroke-width', this.borderWidth);
    this.foregroundSelection
      .selectAll('circle')
      .data([1])
      .join((enter) => enter
        .append('circle')
        .style('stroke-dasharray', `0 ${Math.PI * this.radius * 2}`)
        .attr('r', this.radius)
        .style('stroke-width', this.borderWidth)
        .attr('transform', 'rotate(-90)')
        .style('fill', 'none'))
      .transition()
      .duration(300)
      .style('stroke', this.currentColor)
      .style('stroke-dasharray', `${(this.percentage / 100) * Math.PI * this.radius * 2} ${Math.PI * this.radius * 2}`);

    this.labelSelection.html('');
    if (this.label !== undefined) {
      let { label } = this;
      // A+, B-
      if (typeof this.label === 'string' && ['+', '-'].includes(this.label[this.label.length - 1])) {
        label = [this.label.slice(0, -1), this.label[this.label.length - 1]];
      } else if (typeof this.label === 'object' && this.label.length === 1) {
        // eslint-disable-next-line
        label = this.label[0];
      }
      if (typeof label === 'string') {
        const labelText = label === 'string' ? label : label[0];
        this.labelSelection
          .append('text')
          .attr('font-size', this.radius / 1.2)
          .style('text-anchor', 'middle')
          .style('font-weight', 'bold')
          .attr('dy', '0.35em')
          .text(labelText);
      } else if (typeof label === 'object' && label.length === 2) {
        this.labelSelection
          .append('text')
          .attr('font-size', this.radius / 1.2)
          .style('text-anchor', 'middle')
          .style('font-weight', 'bold')
          .attr('dy', '0.35em')
          .selectAll('tspan')
          .data(label)
          .join('tspan')
          .attr('baseline-shift', (d: string, i: number) => (i === 1 ? 'super' : 'normal'))
          .attr('font-size', (d: string, i: number) => (i === 1 ? this.radius / 4 : this.radius / 1.2))
          .attr('dx', (d: string, i: number) => (i === 1 ? 4 : 0))
          .text((d: string) => d);
      }
    }
  }

  updateData(percentage: number, label?: string | [string] | [string, string]) {
    if (label) {
      this.label = label;
    }
    this.percentage = percentage;
    this.currentColor = this.getCurrentColor();
    this.draw();
  }

  clear() {
    d3.select(this.target).html('');
  }

  exportToPng() {
    exportSvgAsPng(this.svg.node(), {
      scale: 2,
      fileName: this.exportFileName,
      title: this.exportTitle,
    });
  }
}
